import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/Brand";
import { StepBlock, Field, inputCls, CheckRow } from "@/components/FormBits";
import { EmailInput } from "@/components/EmailInput";
import { typoFix } from "@/data/emailDomains";
import { DestinationInput } from "@/components/DestinationInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEPARTURES, DEFAULT_CITY, activeCities } from "@/config/site";
import { saveMember, getMember } from "@/services/demoMember";

const selectCls = "h-12 rounded-2xl border-navy/15 bg-ivory/60 px-4 text-[15px] text-navy focus:ring-4 focus:ring-gold/15";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Register() {
  const nav = useNavigate();
  const m = getMember();
  const [country, setCountry] = useState("FR");
  const [city, setCity] = useState(DEFAULT_CITY);
  const [dream, setDream] = useState(m.dream || "");
  const [civility, setCivility] = useState(m.civility || "");
  const [lastName, setLastName] = useState(m.lastName || "");
  const [firstName, setFirstName] = useState(m.firstName || "");
  const [email, setEmailRaw] = useState(m.email || "");
  const [cgu, setCgu] = useState(!!m.lastName);
  const [promo, setPromo] = useState(!!m.promoOptIn);
  const [errors, setErrors] = useState({});
  const setEmail = (v) => { setEmailRaw(v); setErrors((e) => ({ ...e, email: undefined })); };
  const cities = activeCities(country);

  const submit = (e) => {
    e.preventDefault();
    const err = {};
    if (!dream.trim()) err.dream = "Indiquez votre destination de rêve.";
    if (!civility) err.civility = "Choisissez votre civilité.";
    if (!lastName.trim()) err.lastName = "Indiquez votre nom.";
    if (!firstName.trim()) err.firstName = "Indiquez votre prénom.";
    if (!EMAIL_RE.test(email)) err.email = "Indiquez une adresse e-mail valide.";
    else if (typoFix(email)) err.email = "Vérifiez l'adresse : le nom de domaine semble mal orthographié.";
    if (!cgu) err.cgu = "Vous devez accepter les conditions générales pour continuer.";
    setErrors(err);
    if (Object.keys(err).length) return;
    saveMember({ country, city, dream: dream.trim(), civility, lastName: lastName.trim(), firstName: firstName.trim(), email: email.toLowerCase(), method: "email", promoOptIn: promo, cancelled: false, signupId: "" });
    nav("/abonnement");
  };

  return (
    <div className="min-h-screen bg-ivory">
      <header className="flex justify-center px-5 pb-4 pt-8 sm:pt-12">
        <Logo tone="dark" />
      </header>
      <form onSubmit={submit} noValidate className="mx-auto max-w-xl space-y-5 px-4 pb-16 sm:px-6" data-testid="register-form">
        <p className="mb-2 text-center text-sm text-navy/60">Rejoignez le club en deux étapes. Aucune date à indiquer.</p>

        <StepBlock step="Étape 1 sur 2" title="D'où partez-vous ?" testId="register-step-1">
          <Field label="Pays" htmlFor="country">
            <Select value={country} onValueChange={(v) => { setCountry(v); setCity(activeCities(v)[0]?.code || ""); }}>
              <SelectTrigger id="country" data-testid="step1-country-select" className={selectCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEPARTURES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Aéroport de départ" htmlFor="city" hint="Pour le lancement, toutes nos offres partent de Paris-Charles de Gaulle (CDG). D'autres aéroports arriveront prochainement.">
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger id="city" data-testid="step1-city-select" className={selectCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {cities.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`city-option-${c.code}`}>{c.airport || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Quelle est votre destination de rêve ?" htmlFor="dream" error={errors.dream} hint="Vous continuerez à recevoir les offres vers toutes les destinations.">
            <DestinationInput value={dream} onChange={(v) => { setDream(v); setErrors((e) => ({ ...e, dream: undefined })); }} />
          </Field>
        </StepBlock>

        <StepBlock step="Étape 2 sur 2" title="Vos coordonnées" testId="register-step-2" delay={0.12}>
          <Field label="Civilité" htmlFor="civility" error={errors.civility}>
            <Select value={civility} onValueChange={(v) => { setCivility(v); setErrors((e) => ({ ...e, civility: undefined })); }}>
              <SelectTrigger id="civility" data-testid="step2-civility-select" className={selectCls}><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Mme" data-testid="civility-option-mme">Madame</SelectItem>
                <SelectItem value="M." data-testid="civility-option-m">Monsieur</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Nom" htmlFor="lastName" error={errors.lastName}>
              <input id="lastName" autoComplete="family-name" maxLength={60} value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="step2-lastname-input" className={inputCls} />
            </Field>
            <Field label="Prénom" htmlFor="firstName" error={errors.firstName}>
              <input id="firstName" autoComplete="given-name" maxLength={60} value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="step2-firstname-input" className={inputCls} />
            </Field>
          </div>
          <Field label="Adresse e-mail" htmlFor="email" error={errors.email} hint="Les offres seront envoyées à cette adresse.">
            <EmailInput value={email} onChange={setEmail} />
          </Field>
        </StepBlock>

        <div className="space-y-4 px-1 pt-3">
          <CheckRow id="register-cgu-checkbox" checked={cgu} onChange={setCgu} error={errors.cgu}>
            J'accepte les <Link to="/garantie" className="underline">conditions</Link> et la <Link to="/confidentialite" className="underline" data-testid="register-privacy-link">politique de confidentialité</Link>.
          </CheckRow>
          <CheckRow id="register-promo-checkbox" checked={promo} onChange={setPromo}>
            J'accepte de recevoir des e-mails promotionnels (facultatif).
          </CheckRow>
        </div>

        <button type="submit" data-testid="register-submit-button" className="btn-navy group w-full justify-center py-4 text-base">
          Continuer
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </form>
    </div>
  );
}
